import * as React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CssBaseline,
  Divider,
  List,
  ListItem,
  Avatar,
  Typography,
  Skeleton,
  Stack,
  Paper,
  alpha,
} from "@mui/material";
import {
  GroupAdd,
  Forum,
  HourglassEmpty,
  CheckCircleOutline,
  AccessTime,
  PersonOutline,
  ArrowForward,
  PeopleAlt,
  AssignmentTurnedIn,
  Chat,
  School,
  TrendingUp,
  EventNote,
  CalendarMonth,
} from "@mui/icons-material";

import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

// ── Brand palette ─────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",         // deep navy
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",       // deep green
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

// ── Reusable panel with gradient accent strip on top ─────────────────────────
const DashCard = ({ children, accent = C.primary, sx = {} }) => (
  <Card
    sx={{
      position: "relative",
      borderRadius: 3,
      border: `1px solid ${C.border}`,
      bgcolor: C.paper,
      boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
      overflow: "hidden",
      transition: "box-shadow 0.2s ease",
      "&:hover": {
        boxShadow: "0 6px 20px rgba(13,17,23,0.07)",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: 0, right: 0,
        height: "3px",
        background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
      },
      ...sx,
    }}
  >
    {children}
  </Card>
);

// ── Solid colored stat tile (gradient background) ────────────────────────────
const StatTile = ({ icon, label, value, accent, loading }) => {
  const darkAccent =
    accent === C.primary ? C.primaryDark :
    accent === C.secondary ? C.secondaryDark :
    accent;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${accent} 0%, ${darkAccent} 100%)`,
        color: "#fff",
        transition: "all 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 10px 24px ${accent}40`,
        },
        // Decorative corner orb
        "&::after": {
          content: '""',
          position: "absolute",
          top: "-30%", right: "-15%",
          width: 110, height: 110,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          pointerEvents: "none",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.5}
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            width: 42, height: 42,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.18)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            backdropFilter: "blur(4px)",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 10.5, sm: 11.5 },
              color: "rgba(255,255,255,0.85)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.8,
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 26, sm: 30 },
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.05,
              letterSpacing: -0.5,
            }}
          >
            {loading ? "—" : value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

// ── Status chip styled to brand palette ──────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    pending: { label: "Pending", bg: C.warningSoft, fg: C.warning },
    accepted: { label: "Accepted", bg: C.secondarySoft, fg: C.secondary },
    rejected: { label: "Rejected", bg: C.errorSoft, fg: C.error },
  };
  const s = map[status] || { label: status, bg: "#F3F4F6", fg: C.textSecondary };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        bgcolor: s.bg,
        color: s.fg,
        border: "none",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ── Section header inside a panel ────────────────────────────────────────────
const SectionHeader = ({ title, count, action, onAction, accent = C.primary }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    mb={2}
    sx={{ pt: 2.5, px: { xs: 2.5, sm: 3 } }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography
        sx={{
          fontSize: { xs: 15, sm: 16 },
          fontWeight: 700,
          color: C.textPrimary,
          letterSpacing: -0.2,
        }}
      >
        {title}
      </Typography>
      {count !== undefined && count > 0 && (
        <Chip
          label={count}
          size="small"
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 700,
            bgcolor: accent === C.secondary ? C.secondarySoft : C.primarySoft,
            color: accent === C.secondary ? C.secondary : C.primary,
            "& .MuiChip-label": { px: 1 },
          }}
        />
      )}
    </Stack>
    {action && (
      <Button
        size="small"
        endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
        onClick={onAction}
        sx={{
          textTransform: "none",
          fontSize: 12,
          fontWeight: 600,
          color: accent === C.secondary ? C.secondary : C.primary,
          "&:hover": {
            bgcolor: accent === C.secondary ? C.secondarySoft : C.primarySoft,
          },
        }}
      >
        {action}
      </Button>
    )}
  </Stack>
);

// ── Quick action tile — clickable shortcut into a key mentor workflow ────────
const QuickActionTile = ({ icon, label, accent = C.primary, badge, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    }}
    sx={{
      position: "relative",
      p: { xs: 1.5, sm: 2 },
      borderRadius: 3,
      border: `1px solid ${C.border}`,
      bgcolor: C.paper,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      gap: { xs: 1, sm: 1.25 },
      minHeight: { xs: 88, sm: 104 },
      userSelect: "none",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: alpha(accent, 0.45),
        boxShadow: `0 8px 20px ${alpha(accent, 0.18)}`,
        transform: "translateY(-3px)",
      },
      "&:hover .qa-icon": {
        background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
        color: "#fff",
      },
      "&:focus-visible": {
        outline: `2px solid ${accent}`,
        outlineOffset: 2,
      },
    }}
  >
    {!!badge && (
      <Box
        sx={{
          position: "absolute",
          top: 10, right: 10,
          minWidth: 20, height: 20,
          px: badge > 9 ? 0.6 : 0,
          borderRadius: "50%",
          bgcolor: C.warning,
          color: "#fff",
          fontSize: 10.5,
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 2px 6px ${alpha(C.warning, 0.4)}`,
        }}
      >
        {badge > 99 ? "99+" : badge}
      </Box>
    )}
    <Box
      className="qa-icon"
      sx={{
        width: { xs: 34, sm: 38 },
        height: { xs: 34, sm: 38 },
        borderRadius: 2,
        bgcolor: alpha(accent, 0.1),
        color: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease",
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: { xs: 12, sm: 13 },
        fontWeight: 700,
        color: C.textPrimary,
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
  </Paper>
);

export default function MentorDashboard() {
  const navigate = useNavigate();

  // ── API state ──────────────────────────────────────────────────────────
  const [communityPosts, setCommunityPosts] = React.useState([]);
  const [loadingPosts, setLoadingPosts] = React.useState(false);
  const [errorPosts, setErrorPosts] = React.useState("");
  const [requests, setRequests] = React.useState([]);
  const [learners, setLearners] = React.useState([]);
  const [loadingRequests, setLoadingRequests] = React.useState(false);
  const [errorRequests, setErrorRequests] = React.useState("");

  // ── Fetch community posts ──────────────────────────────────────────────
  React.useEffect(() => {
    (async () => {
      try {
        setLoadingPosts(true);
        const res = await axios.get("https://backend-global-1vq9.onrender.com/api/posts");
        setCommunityPosts(res.data);
      } catch { setErrorPosts("Failed to load community feed"); }
      finally { setLoadingPosts(false); }
    })();
  }, []);

  // ── Fetch mentor requests ──────────────────────────────────────────────
  React.useEffect(() => {
    (async () => {
      try {
        setLoadingRequests(true);
        const user = JSON.parse(localStorage.getItem("user"));
        const mentorId = user?.id || user?._id;
        if (!mentorId) { setErrorRequests("Mentor not found."); return; }
        const res = await axios.get(
          `https://backend-global-1vq9.onrender.com/api/connection-requests/mentor/${mentorId}`
        );
        setRequests(res.data?.data || []);
      } catch { setErrorRequests("Failed to load mentee requests."); }
      finally { setLoadingRequests(false); }
    })();
  }, []);

  // ── Fetch learners ─────────────────────────────────────────────────────
  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("https://backend-global-1vq9.onrender.com/api/learner/list");
        setLearners(res.data?.learners || []);
      } catch { setErrorRequests("Failed to load learner data."); }
    })();
  }, []);

  const enrichedRequests = requests
    .map((req) => {
      const mentee = learners.find((l) => l.userId === req.menteeId);
      return {
        ...req,
        menteeName: mentee?.fullName || "Unknown",
        menteeEmail: mentee?.email || "No email",
        menteeLocation: mentee?.location || "N/A",
        menteeGoals: mentee?.goals || "Not specified",
      };
    })
    .filter((r) => r.menteeName !== "Unknown");

  // ── Stats derived from data ────────────────────────────────────────────
  const pendingCount = enrichedRequests.filter((r) => r.status === "pending").length;
  const acceptedCount = enrichedRequests.filter((r) => r.status === "accepted").length;

  // ── Quick actions — curated shortcuts from the mentor side nav ──────────
  const quickActions = [
    { label: "Mentees", icon: <PeopleAlt sx={{ fontSize: 20 }} />, path: "/learner-list", accent: C.primary },
    { label: "Requests", icon: <AssignmentTurnedIn sx={{ fontSize: 20 }} />, path: "/mentorsiderequest", accent: C.warning, badge: pendingCount },
    { label: "Chats", icon: <Chat sx={{ fontSize: 20 }} />, path: "/chat", accent: C.primary },
    { label: "Lessons", icon: <School sx={{ fontSize: 20 }} />, path: "/createLessons", accent: C.secondary }
  ];

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const firstName = user?.fullName?.split(" ")[0];

  return (
    <Box sx={{ display: "flex", bgcolor: C.bg, minHeight: "100vh", width: "100%", boxSizing: "border-box" }}>
      <CssBaseline />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <TopBar />

        <Box sx={{
          flexGrow: 1,
          px: { xs: "12px", sm: "20px", md: "32px" },
          py: { xs: 2, sm: 3, md: 4 },
          width: "100%",
          boxSizing: "border-box",
        }}>

          {/* ── HERO HEADER ─────────────────────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              borderRadius: 3,
              overflow: "hidden",
              background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.primaryDark} 100%)`,
              p: { xs: 3, sm: 4, md: 5 },
              mb: { xs: 2.5, sm: 3.5 },
              color: "#fff",
              boxShadow: "0 6px 24px rgba(10,46,138,0.18)",
            }}
          >
            {/* Decorative green orb */}
            <Box sx={{
              position: "absolute",
              top: "-40%", right: "-10%",
              width: { xs: 200, sm: 280 },
              height: { xs: 200, sm: 280 },
              borderRadius: "50%",
              background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
              pointerEvents: "none",
            }} />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography sx={{
                fontSize: { xs: 11, sm: 12 },
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.7)",
                mb: 1,
              }}>
                Mentor Dashboard
              </Typography>
              <Typography sx={{
                fontWeight: 800,
                fontSize: { xs: 24, sm: 32, md: 40 },
                color: "#fff",
                lineHeight: 1.15,
                mb: { xs: 1, sm: 1.5 },
                letterSpacing: -0.5,
              }}>
                Welcome back{firstName ? `, ${firstName}` : ""} 
              </Typography>
              <Typography sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: { xs: 13, sm: 15, md: 16 },
                maxWidth: 520,
                lineHeight: 1.5,
              }}>
                Here's what's happening with your mentees today — review pending requests and stay close to the community.
              </Typography>
            </Box>
          </Paper>

          {/* ── Quick Actions ───────────────────────────────────────── */}
          <Box mb={{ xs: 2.5, sm: 3.5 }}>
            <Typography sx={{
              fontSize: { xs: 15, sm: 16 },
              fontWeight: 700,
              color: C.textPrimary,
              letterSpacing: -0.2,
              mb: { xs: 1.25, sm: 1.5 },
            }}>
              Quick Actions
            </Typography>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(4, 1fr)",
              },
              gap: { xs: 1.25, sm: 1.5, md: 2 },
            }}>
              {quickActions.map((qa) => (
                <QuickActionTile
                  key={qa.path}
                  icon={qa.icon}
                  label={qa.label}
                  accent={qa.accent}
                  badge={qa.badge}
                  onClick={() => navigate(qa.path)}
                />
              ))}
            </Box>
          </Box>

          {/* ── Stat row — solid colored tiles ─────────────────────── */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1.5, sm: 2 }}
            mb={{ xs: 2.5, sm: 3.5 }}
          >
            <StatTile
              icon={<HourglassEmpty sx={{ fontSize: 22 }} />}
              label="Pending Requests"
              value={pendingCount}
              accent={C.primary}
              loading={loadingRequests}
            />
            <StatTile
              icon={<CheckCircleOutline sx={{ fontSize: 22 }} />}
              label="Active Mentees"
              value={acceptedCount}
              accent={C.primary}
              loading={loadingRequests}
            />
            <StatTile
              icon={<Forum sx={{ fontSize: 22 }} />}
              label="Community Posts"
              value={communityPosts.length}
              accent={C.primary}
              loading={loadingPosts}
            />
          </Stack>

          {/* ── Two-column section ─────────────────────────────────── */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 2, sm: 2.5, md: 3 },
            width: "100%",
          }}>

            {/* ═══ Mentee Requests ═══ */}
            <DashCard accent={C.primary}>
              <SectionHeader
                title="Mentee Requests"
                count={pendingCount}
                action={enrichedRequests.length > 3 ? "View all" : null}
                onAction={() => navigate("/mentorsiderequest")}
                accent={C.secondary}
              />
              <CardContent sx={{ pt: 0, px: { xs: 2.5, sm: 3 }, pb: { xs: 2, sm: 2.5 } }}>
                {loadingRequests ? (
                  <Stack spacing={1.5}>
                    {[1, 2, 3].map((i) => (
                      <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={42} height={42} />
                        <Box flex={1}>
                          <Skeleton width="60%" height={14} />
                          <Skeleton width="40%" height={12} />
                        </Box>
                        <Skeleton variant="rounded" width={70} height={28} />
                      </Stack>
                    ))}
                  </Stack>
                ) : errorRequests ? (
                  <Box sx={{
                    p: 2, borderRadius: 2,
                    bgcolor: C.errorSoft,
                    color: C.error,
                    fontSize: 13,
                  }}>
                    {errorRequests}
                  </Box>
                ) : enrichedRequests.length === 0 ? (
                  <Stack alignItems="center" spacing={1} py={3}>
                    <GroupAdd sx={{ fontSize: 36, color: C.textMuted, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: 13, color: C.textSecondary }}>
                      No mentee requests yet
                    </Typography>
                  </Stack>
                ) : (
                  <List sx={{ p: 0 }}>
                    {enrichedRequests.slice(0, 3).map((req, i) => (
                      <React.Fragment key={req._id}>
                        <ListItem sx={{
                          px: 0,
                          py: 1.25,
                          gap: 1.5,
                          alignItems: "center",
                          flexWrap: { xs: "wrap", sm: "nowrap" },
                          borderRadius: 2,
                          transition: "all 0.15s",
                          "&:hover": { bgcolor: C.primarySofter, transform: "translateX(2px)" },
                        }}>
                          <Avatar
                            sx={{
                              width: { xs: 38, sm: 44 },
                              height: { xs: 38, sm: 44 },
                              flexShrink: 0,
                              background: `linear-gradient(135deg, ${C.secondary}, ${C.primaryLight})`,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: { xs: 14, sm: 16 },
                              border: `2px solid ${C.primarySoft}`,
                            }}
                          >
                            {req.menteeName?.charAt(0)?.toUpperCase() || "M"}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1} mb={0.25} flexWrap="wrap">
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: 13, sm: 14 },
                                  color: C.textPrimary,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: { xs: 130, sm: "100%" },
                                }}
                              >
                                {req.menteeName}
                              </Typography>
                              <StatusChip status={req.status} />
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <AccessTime sx={{ fontSize: 11, color: C.textMuted }} />
                              <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: C.textMuted }}>
                                {new Date(req.createdAt).toLocaleDateString("en-KE", {
                                  month: "short", day: "numeric", year: "numeric",
                                })}
                              </Typography>
                            </Stack>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              fontSize: { xs: 11, sm: 12 },
                              fontWeight: 700,
                              px: { xs: 1.5, sm: 2 },
                              py: 0.5,
                              bgcolor: C.secondary,
                              color: "#fff",
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: C.primaryDark,
                                boxShadow: `0 4px 12px ${C.primary}40`,
                              },
                              flexShrink: 0,
                              ml: { xs: "auto", sm: 0 },
                            }}
                            onClick={() => navigate("/mentorsiderequest", {
                              state: {
                                menteeId: req.menteeId,
                                menteeName: req.menteeName,
                                menteeEmail: req.menteeEmail,
                                menteeLocation: req.menteeLocation,
                                menteeGoals: req.menteeGoals,
                              },
                            })}
                          >
                            Review
                          </Button>
                        </ListItem>
                        {i < Math.min(enrichedRequests.length, 3) - 1 && (
                          <Divider sx={{ borderColor: C.borderSoft }} />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </DashCard>

            {/* ═══ Community Feed ═══ */}
            <DashCard accent={C.secondary}>
              <SectionHeader
                title="Community Feed"
                count={communityPosts.length}
                action={communityPosts.length > 4 ? "Explore" : null}
                onAction={() => navigate("/discussionForum")}
                accent={C.secondary}
              />
              <CardContent sx={{ pt: 0, px: { xs: 2.5, sm: 3 }, pb: { xs: 2, sm: 2.5 } }}>
                {loadingPosts ? (
                  <Stack spacing={1.5}>
                    {[1, 2, 3].map((i) => (
                      <Stack key={i} direction="row" spacing={1.5}>
                        <Skeleton variant="rounded" width={40} height={40} />
                        <Box flex={1}>
                          <Skeleton width="90%" height={14} />
                          <Skeleton width="40%" height={12} />
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                ) : errorPosts ? (
                  <Box sx={{
                    p: 2, borderRadius: 2,
                    bgcolor: C.errorSoft,
                    color: C.error,
                    fontSize: 13,
                  }}>
                    {errorPosts}
                  </Box>
                ) : communityPosts.length === 0 ? (
                  <Stack alignItems="center" spacing={1} py={3}>
                    <Forum sx={{ fontSize: 36, color: C.textMuted, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: 13, color: C.textSecondary }}>
                      No community posts yet
                    </Typography>
                  </Stack>
                ) : (
                  <List sx={{ p: 0 }}>
                    {communityPosts.slice(0, 4).map((post, i) => (
                      <React.Fragment key={post._id}>
                        <ListItem
                          sx={{
                            px: 0,
                            py: 1.25,
                            gap: 1.5,
                            alignItems: "flex-start",
                            borderRadius: 2,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            "&:hover": { bgcolor: C.secondarySofter, transform: "translateX(2px)" },
                          }}
                          onClick={() => navigate("/discussionForum")}
                        >
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: { xs: 38, sm: 42 },
                              height: { xs: 38, sm: 42 },
                              flexShrink: 0,
                              background: post.mediaType === "image"
                                ? "transparent"
                                : `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
                              color: "#fff",
                              border: `1px solid ${C.border}`,
                            }}
                            src={post.mediaType === "image" ? post.mediaUrl : undefined}
                          >
                            <PersonOutline sx={{ fontSize: 22 }} />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                mb: 0.5,
                                fontSize: { xs: 12, sm: 13 },
                                fontWeight: 700,
                                color: C.textPrimary,
                                lineHeight: 1.4,
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {post.title}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: { xs: 10, sm: 11 },
                                color: C.secondary,
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.25,
                              }}
                            >
                              View post <ArrowForward sx={{ fontSize: 11 }} />
                            </Typography>
                          </Box>
                        </ListItem>
                        {i < Math.min(communityPosts.length, 4) - 1 && (
                          <Divider sx={{ borderColor: C.borderSoft }} />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </DashCard>
          </Box>

        </Box>

        <Footer />
      </Box>
    </Box>
  );
}